import React, { useMemo, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Box, Typography } from '@mui/material';
import { MainContainer } from '../../../components/ContentWrapper';
import { QrList } from './QrList';
import config from '../../../config';
import { qrCodeEndpoint } from '../../../api/endpoints';
import axiosInstant from '../../../api';
import { IQrEntry } from './constants';
import { WhiteBackground } from '../../../components/WhiteBackground';
import { PageTitle } from '../../../components/PageTitle';
import { Button } from '../../../components/Button/Button';
import { IconPlus } from '../../../utils/icons';
import { DotLottleLoader, LoadMoreBtn, PageDivider } from '../../../components/Loader';
import { SearchPanel } from './SearchPanel';
import { QrForm } from './QrForm';
import SortControls from '../../../components/SortControls/SortControls';
import { useCommonParams } from '../../../hooks/useCommonParams';
import { sortData } from '../../../utils/sortUtils';
import { usePagination } from '../../../hooks/usePagination';

const PAGE_SIZE = 20;

const QrGenerator = () => {
  const {
    searchParams,
    setSearchParams,
    page,
    setPage,
    sortType,
    setSortType,
    setSortDirection,
  } = useCommonParams({ defaultSortKey: "dateCreated" });

  const [selectedQr, setSelectedQr] = useState<IQrEntry>();
  const [showQrForm, setShowQrForm] = useState(false);

  const searchText = searchParams.get('search') || '';

  const openCreationModal = (qr?: IQrEntry) => {
    setSelectedQr(qr);
    setShowQrForm(true);
  };

  const fetchFn = useCallback(
    async (pageNum: number) => {
      try {
        const params: Record<string, string> = {
          sort: 'id,name',
          page: String(pageNum),
          limit: String(PAGE_SIZE),
        };
        if (searchText) params.search = searchText;

        const url = `${config.API_URL}${qrCodeEndpoint}?${new URLSearchParams(params).toString()}`;
        const response = await axiosInstant.get(url);
        const rows = response.data.rows ?? [];
        const count = response.data.count ?? 0;
        return { rows, count, error: null };
      } catch (err) {
        toast.error('Failed to fetch QR codes');
        return { rows: [], count: 0, error: 'Failed to fetch QR codes' };
      }
    },
    [searchText],
  );

  const {
    items: data,
    totalCount: count,
    loading: isLoading,
    hasMore,
    canLoadPrevious,
    loadingPrevious,
    initLoad,
    loadMore,
    loadPrevious,
    topSentinelRef,
  } = usePagination<IQrEntry>({
    fetchFn,
    pageSize: PAGE_SIZE,
    deepLink: {
      page,
      setPage,
      searchParams,
      setSearchParams,
      ignoreParams: ["sort", "page"],
    },
  });

  const sortedData = useMemo(() => {
    const key = sortType.key === 'dateCreated' ? 'id' : sortType.key;
    return sortData(data, key, sortType.isAscending);
  }, [data, sortType]);

  return (
    <WhiteBackground>
      <MainContainer>
        <Box className="flex flex-wrap items-center justify-between gap-3">
          <PageTitle title="QR Codes" />
          <Button
            className="!flex gap-2 !px-3 sm:!px-4 !text-sm sm:!text-base shrink-0"
            onClick={() => openCreationModal()}
          >
            <IconPlus />
            <Typography className="!text-sm sm:!text-base">Create QR Code</Typography>
          </Button>
        </Box>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6 sm:mt-8">
          <SearchPanel searchParams={searchParams} setSearchParams={setSearchParams} />
          <div className="flex justify-end">
            <SortControls
              isAscending={sortType.isAscending}
              onDirectionChange={setSortDirection}
            />
          </div>
        </div>
        <PageDivider
          count={count}
          label="QR Codes"
          className="mt-8"
          canLoadPrevious={canLoadPrevious}
          hasMore={hasMore}
          onLoadPrevious={loadPrevious}
        />
        <Box className="mt-10">
          {canLoadPrevious && <div ref={topSentinelRef} style={{ height: 1 }} />}
          {loadingPrevious && (
            <div className="flex justify-center py-2">
              <DotLottleLoader fill={false} sx={{ width: 100, height: 100 }} />
            </div>
          )}
          <QrList
            list={sortedData}
            onEdit={openCreationModal}
            onUpdate={() => initLoad(page)}
            onRemove={() => initLoad(page)}
          />
        </Box>
        {isLoading && data.length === 0 && <DotLottleLoader />}
        {sortedData.length > 0 && hasMore && (
          <LoadMoreBtn onClick={loadMore} loading={isLoading} />
        )}
        {showQrForm && (
          <QrForm
            entry={selectedQr}
            onUpdate={() => initLoad(page)}
            onClose={() => {
              setSelectedQr(undefined);
              setShowQrForm(false);
            }}
          />
        )}
      </MainContainer>
    </WhiteBackground>
  );
};

export default QrGenerator;
